// @ts-nocheck
import { supabase } from '@/integrations/supabase/client';

// Interface para dados financeiros de processos administrativos
export interface AdministrativeProcessFinancialData {
  // Dados básicos do processo
  processId: string;
  processTitle: string;
  processValue: number;
  clientId: string;
  clientName: string;

  // Configuração de honorários
    hasInitialFee: boolean;
    initialFeeAmount: number;
  initialFeeType: 'percentage' | 'fixed';
  initialFeeBaseValue: number;
  initialFeePaymentStatus: 'pending' | 'paid' | 'partial';

    hasSuccessFee: boolean;
    successFeeType: 'percentage_global' | 'percentage_installment' | 'fixed_global' | 'fixed_monthly';
    successFeeValue: number;
    successFeeTrigger: 'decisao' | 'deferimento' | 'indeferimento' | 'recurso_procedente' | 'acordo' | 'outro';
  successFeeCalculationBase: 'total' | 'per_installment';

  // Configuração do benefício
  benefitValue: number;
  benefitValueType: 'estimated' | 'confirmed' | 'undefined';
  benefitPaymentType: 'lump_sum' | 'installments' | 'undefined';
  benefitInstallmentCount: number;
  benefitInstallmentValue: number;
  benefitFirstPaymentDate?: Date;
  benefitFirstPaymentDatePending: boolean;

  // Benefício indefinido (específico para BPC/LOAS/Aposentadoria/Pensão)
  isBenefitUndefined: boolean;
  undefinedBenefitFirstInstallmentsCount: number;
  undefinedBenefitFirstInstallmentValue: number;

  // Pagamento retroativo
  hasRetroactivePayment: boolean;
  retroactivePaymentValue: number;
  retroactivePaymentType: 'percentage' | 'fixed';
  retroactivePaymentDate?: Date;
  retroactivePaymentDatePending: boolean;
  chargeOnRetroactive: boolean;
  chargeOnInstallments: boolean;
  chargeOnBoth: boolean;

  // Pro Bono
  isProBono: boolean;

  // Honorários de serviços (sem benefício)
  isSuccessFeeIndependent: boolean;
  successFeeIndependentValue: number;
  successFeeIndependentInstallmentCount: number;
  successFeeIndependentInstallmentFrequency: 'mensal' | 'bimestral' | 'trimestral' | 'semestral' | 'anual';
  successFeeIndependentFirstPaymentDate?: Date;
  successFeeIndependentFirstPaymentDatePending: boolean;
  successFeeIndependentAlreadyPaid: boolean;
  successFeeIndependentPaidAmount: number;
  successFeeIndependentPaidDate?: Date;

  // Configuração completa em JSON
  feeConfigurationJson?: any;
}

// Interface para entrada financeira
export interface FinancialEntry {
  id?: string;
  description: string;
  amount: number;
  entry_type: 'receita' | 'despesa';
  category: string;
  payment_date?: string;
  due_date?: string;
  status: string;
  client_id?: string;
  administrative_process_id?: string;
  payment_confirmed?: boolean;
  is_negotiating?: boolean;
  is_installment?: boolean;
  installments_count?: number;
  installment_frequency?: string;
  down_payment?: number;
  is_conditional_payment?: boolean;
  conditional_type?: string;
  actual_payment_date?: string;
  payment_notes?: string;
  user_id: string;
  created_at?: string;
  updated_at?: string;
}

export class AdministrativeFinancialIntegrationService {
  /**
   * Cria integração financeira para um processo administrativo
   */
  static async createFinancialIntegration(processId: string): Promise<void> {
    try {
      // Buscar dados do processo
      const { data: processData, error } = await supabase
        .from('administrative_processes')
        .select(`
          id,
          title,
          case_value,
          client_id,
          clients!inner(name),
          fee_configuration,
          benefit_value_type,
          is_benefit_undefined,
          undefined_benefit_first_installments_count,
          undefined_benefit_first_installment_value,
          has_retroactive_payment,
          retroactive_payment_value,
          retroactive_payment_type,
          retroactive_payment_date,
          retroactive_payment_date_pending,
          charge_on_retroactive,
          charge_on_installments,
          charge_on_both,
          is_pro_bono,
          has_initial_fee,
          initial_fee_amount,
          initial_fee_type,
          initial_fee_payment_status,
          has_success_fee,
          success_fee_type,
          success_fee_value,
          success_fee_trigger,
          benefit_payment_type,
          benefit_installment_count,
          benefit_installment_value,
          benefit_first_payment_date,
          benefit_first_payment_date_pending,
          is_success_fee_independent,
          success_fee_independent_value,
          success_fee_independent_installment_count,
          success_fee_independent_installment_frequency,
          success_fee_independent_first_payment_date,
          success_fee_independent_first_payment_date_pending,
          success_fee_independent_already_paid,
          success_fee_independent_paid_amount,
          success_fee_independent_paid_date
        `)
        .eq('id', processId)
        .single();

      if (error) {
        throw new Error(`Erro ao buscar processo administrativo: ${error.message}`);
      }

      if (!processData) {
        throw new Error('Processo administrativo não encontrado');
      }

      // Converter dados para o formato esperado
      const financialData: AdministrativeProcessFinancialData = {
        processId: processData.id,
        processTitle: processData.title,
        processValue: processData.case_value || 0,
        clientId: processData.client_id,
        clientName: processData.clients.name,
        feeConfigurationJson: processData.fee_configuration,
        benefitValueType: processData.benefit_value_type || 'estimated',
        isBenefitUndefined: processData.is_benefit_undefined || false,
        undefinedBenefitFirstInstallmentsCount: processData.undefined_benefit_first_installments_count || 1,
        undefinedBenefitFirstInstallmentValue: processData.undefined_benefit_first_installment_value || 0,
        hasRetroactivePayment: processData.has_retroactive_payment || false,
        retroactivePaymentValue: processData.retroactive_payment_value || 0,
        retroactivePaymentType: processData.retroactive_payment_type || 'percentage',
        retroactivePaymentDate: processData.retroactive_payment_date ? new Date(processData.retroactive_payment_date) : undefined,
        retroactivePaymentDatePending: processData.retroactive_payment_date_pending || false,
        chargeOnRetroactive: processData.charge_on_retroactive || false,
        chargeOnInstallments: processData.charge_on_installments || true,
        chargeOnBoth: processData.charge_on_both || false,
        isProBono: processData.is_pro_bono || false,
        hasInitialFee: processData.has_initial_fee || false,
        initialFeeAmount: processData.initial_fee_amount || 0,
        initialFeeType: processData.initial_fee_type || 'percentage',
        initialFeeBaseValue: processData.case_value || 0,
        initialFeePaymentStatus: processData.initial_fee_payment_status || 'pending',
        hasSuccessFee: processData.has_success_fee || false,
        successFeeType: processData.success_fee_type || 'percentage_global',
        successFeeValue: parseFloat(processData.success_fee_value) || 0,
        successFeeTrigger: processData.success_fee_trigger || 'decisao',
        successFeeCalculationBase: 'total',
        benefitPaymentType: processData.benefit_payment_type || 'lump_sum',
        benefitInstallmentCount: processData.benefit_installment_count || 1,
        benefitInstallmentValue: processData.benefit_installment_value || 0,
        benefitFirstPaymentDate: processData.benefit_first_payment_date ? new Date(processData.benefit_first_payment_date) : undefined,
        benefitFirstPaymentDatePending: processData.benefit_first_payment_date_pending || false,
        benefitValue: processData.case_value || 0,
        // NOVOS CAMPOS: Honorários de serviços
        isSuccessFeeIndependent: processData.is_success_fee_independent || false,
        successFeeIndependentValue: parseFloat(processData.success_fee_independent_value) || 0,
        successFeeIndependentInstallmentCount: processData.success_fee_independent_installment_count || 1,
        successFeeIndependentInstallmentFrequency: processData.success_fee_independent_installment_frequency || 'mensal',
        successFeeIndependentFirstPaymentDate: processData.success_fee_independent_first_payment_date ? new Date(processData.success_fee_independent_first_payment_date) : undefined,
        successFeeIndependentFirstPaymentDatePending: processData.success_fee_independent_first_payment_date_pending || false,
        successFeeIndependentAlreadyPaid: processData.success_fee_independent_already_paid || false,
        successFeeIndependentPaidAmount: parseFloat(processData.success_fee_independent_paid_amount) || 0,
        successFeeIndependentPaidDate: processData.success_fee_independent_paid_date ? new Date(processData.success_fee_independent_paid_date) : undefined
      };

      // Processar integração financeira
      await this.processAdministrativeProcessFinancialIntegration(processId, financialData);
    } catch (error) {
      console.error('Erro ao criar integração financeira:', error);
      throw error;
    }
  }

  /**
   * Atualiza integração financeira para um processo administrativo
   */
  static async updateFinancialIntegration(processId: string): Promise<void> {
    try {
      // Remover entradas financeiras existentes
      const { error: deleteError } = await supabase
        .from('financial_entries')
        .delete()
        .eq('administrative_process_id', processId);

      if (deleteError) {
        console.warn('Erro ao remover entradas financeiras existentes:', deleteError);
      }

      // Criar nova integração
      await this.createFinancialIntegration(processId);
    } catch (error) {
      console.error('Erro ao atualizar integração financeira:', error);
      throw error;
    }
  }

  /**
   * Processa completamente a integração financeira de um processo administrativo
   */
  static async processAdministrativeProcessFinancialIntegration(
    processId: string,
    financialData: AdministrativeProcessFinancialData
  ): Promise<void> {
    try {
      // Salvar configuração financeira
      await this.saveAdministrativeProcessFinancialConfiguration(processId, financialData);

      // Criar entradas financeiras
      const entries = await this.createFinancialEntriesFromAdministrativeProcess(financialData);

      // Salvar entradas financeiras
      if (entries.length > 0) {
        await this.saveFinancialEntries(entries);
      }
    } catch (error) {
      console.error('Erro na integração financeira do processo administrativo:', error);
      throw error;
    }
  }

  /**
   * Salva a configuração financeira de um processo administrativo
   */
  static async saveAdministrativeProcessFinancialConfiguration(
    processId: string, 
    financialData: AdministrativeProcessFinancialData
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('administrative_processes')
        .update({
          fee_configuration: financialData.feeConfigurationJson,
          benefit_value_type: financialData.benefitValueType,
          is_benefit_undefined: financialData.isBenefitUndefined,
          undefined_benefit_first_installments_count: financialData.undefinedBenefitFirstInstallmentsCount,
          undefined_benefit_first_installment_value: financialData.undefinedBenefitFirstInstallmentValue.toString(),
          has_retroactive_payment: financialData.hasRetroactivePayment,
          retroactive_payment_value: financialData.retroactivePaymentValue.toString(),
          retroactive_payment_type: financialData.retroactivePaymentType,
          retroactive_payment_date: financialData.retroactivePaymentDate?.toISOString().split('T')[0],
          retroactive_payment_date_pending: financialData.retroactivePaymentDatePending,
          charge_on_retroactive: financialData.chargeOnRetroactive,
          charge_on_installments: financialData.chargeOnInstallments,
          charge_on_both: financialData.chargeOnBoth,
          is_pro_bono: financialData.isProBono,
          has_initial_fee: financialData.hasInitialFee,
          initial_fee_amount: financialData.initialFeeAmount.toString(),
          initial_fee_type: financialData.initialFeeType,
          initial_fee_payment_status: financialData.initialFeePaymentStatus,
          has_success_fee: financialData.hasSuccessFee,
          success_fee_type: financialData.successFeeType,
          success_fee_value: financialData.successFeeValue.toString(),
          success_fee_trigger: financialData.successFeeTrigger,
          benefit_payment_type: financialData.benefitPaymentType,
          benefit_installment_count: financialData.benefitInstallmentCount,
          benefit_installment_value: financialData.benefitInstallmentValue.toString(),
          benefit_first_payment_date: financialData.benefitFirstPaymentDate?.toISOString().split('T')[0],
          benefit_first_payment_date_pending: financialData.benefitFirstPaymentDatePending,
          // NOVOS CAMPOS: Honorários de serviços
          is_success_fee_independent: financialData.isSuccessFeeIndependent,
          success_fee_independent_value: financialData.successFeeIndependentValue.toString(),
          success_fee_independent_installment_count: financialData.successFeeIndependentInstallmentCount,
          success_fee_independent_installment_frequency: financialData.successFeeIndependentInstallmentFrequency,
          success_fee_independent_first_payment_date: financialData.successFeeIndependentFirstPaymentDate?.toISOString().split('T')[0],
          success_fee_independent_first_payment_date_pending: financialData.successFeeIndependentFirstPaymentDatePending,
          success_fee_independent_already_paid: financialData.successFeeIndependentAlreadyPaid,
          success_fee_independent_paid_amount: financialData.successFeeIndependentPaidAmount.toString(),
          success_fee_independent_paid_date: financialData.successFeeIndependentPaidDate?.toISOString().split('T')[0],
          case_value: financialData.processValue
        })
        .eq('id', processId);

      if (error) {
        throw new Error(`Erro ao salvar configuração financeira: ${error.message}`);
      }
    } catch (error) {
      console.error('Erro ao salvar configuração financeira do processo administrativo:', error);
      throw error;
    }
  }

  /**
   * Cria entradas financeiras baseadas na configuração do processo administrativo
   */
  static async createFinancialEntriesFromAdministrativeProcess(
    financialData: AdministrativeProcessFinancialData
  ): Promise<FinancialEntry[]> {
    const entries: FinancialEntry[] = [];

    try {
      // Se for Pro Bono, criar apenas entrada de controle
      if (financialData.isProBono) {
        const probonoEntry: FinancialEntry = {
          description: `Pro Bono - ${financialData.processTitle}`,
          amount: 0,
          entry_type: 'receita',
          category: 'Pro Bono',
          status: 'Pago',
          client_id: financialData.clientId,
          administrative_process_id: financialData.processId,
          payment_confirmed: true,
          payment_notes: 'Caso Pro Bono - Sem cobrança de honorários',
          user_id: '00000000-0000-0000-0000-000000000000' // ID de usuário padrão temporário
        };
        entries.push(probonoEntry);
        return entries;
      }

      // Criar entrada de honorários iniciais
      if (financialData.hasInitialFee && financialData.initialFeeAmount > 0) {
        const initialFeeAmount = financialData.initialFeeType === 'percentage'
          ? (financialData.processValue * financialData.initialFeeAmount) / 100
          : financialData.initialFeeAmount;

        const initialFeeEntry: FinancialEntry = {
          description: `Honorários Iniciais - ${financialData.processTitle}`,
          amount: initialFeeAmount,
          entry_type: 'receita',
          category: 'Honorários',
          status: financialData.initialFeePaymentStatus === 'paid' ? 'Pago' : 'Pendente',
          client_id: financialData.clientId,
          administrative_process_id: financialData.processId,
          payment_confirmed: financialData.initialFeePaymentStatus === 'paid',
          payment_notes: `Honorários iniciais - ${financialData.initialFeeType === 'percentage' ? `${financialData.initialFeeAmount}%` : 'Valor fixo'}`,
          user_id: '00000000-0000-0000-0000-000000000000' // ID de usuário padrão temporário
        };
        entries.push(initialFeeEntry);
      }

      // Criar entradas de honorários de serviços (prioridade)
      if (financialData.isSuccessFeeIndependent && financialData.successFeeIndependentValue > 0) {
        const independentFeeAmount = financialData.successFeeIndependentValue;
        const remainingAmount = financialData.successFeeIndependentAlreadyPaid 
          ? independentFeeAmount - financialData.successFeeIndependentPaidAmount 
          : independentFeeAmount;

        // Se já foi pago parcialmente, criar entrada para o valor já pago
        if (financialData.successFeeIndependentAlreadyPaid && financialData.successFeeIndependentPaidAmount > 0) {
          const paidEntry: FinancialEntry = {
            description: `Honorários de Serviços (Pago) - ${financialData.processTitle}`,
            amount: financialData.successFeeIndependentPaidAmount,
            entry_type: 'receita',
            category: 'Honorários',
            status: 'Pago',
            client_id: financialData.clientId,
            administrative_process_id: financialData.processId,
            payment_confirmed: true,
            actual_payment_date: financialData.successFeeIndependentPaidDate?.toISOString().split('T')[0],
            payment_notes: `Honorários de serviços já pagos - Valor total: ${independentFeeAmount}`,
            user_id: '00000000-0000-0000-0000-000000000000'
          };
          entries.push(paidEntry);
        }

        // Criar entrada para o valor restante
        if (remainingAmount > 0) {
          const remainingEntry: FinancialEntry = {
            description: `Honorários de Serviços - ${financialData.processTitle}`,
            amount: remainingAmount,
            entry_type: 'receita',
            category: 'Honorários',
            status: 'Pendente',
            client_id: financialData.clientId,
            administrative_process_id: financialData.processId,
            payment_confirmed: false,
            is_installment: financialData.successFeeIndependentInstallmentCount > 1,
            installments_count: financialData.successFeeIndependentInstallmentCount,
            installment_frequency: financialData.successFeeIndependentInstallmentFrequency,
            due_date: financialData.successFeeIndependentFirstPaymentDate?.toISOString().split('T')[0],
            payment_notes: `Honorários de serviços - ${financialData.successFeeIndependentInstallmentCount} parcelas de ${financialData.successFeeIndependentInstallmentFrequency}`,
            user_id: '00000000-0000-0000-0000-000000000000'
          };
          entries.push(remainingEntry);
        }
      }
      // Criar entradas de honorários de êxito tradicionais (se não for independente)
      else if (financialData.hasSuccessFee && financialData.successFeeValue > 0) {
        if (financialData.isBenefitUndefined) {
          // Para benefício indefinido, criar entradas baseadas nas primeiras parcelas
          const baseValue = financialData.undefinedBenefitFirstInstallmentValue * financialData.undefinedBenefitFirstInstallmentsCount;
          let successFeeAmount = 0;

          if (financialData.successFeeType === 'percentage_global' || financialData.successFeeType === 'percentage_installment') {
            successFeeAmount = (baseValue * financialData.successFeeValue) / 100;
          } else if (financialData.successFeeType === 'fixed_global') {
            successFeeAmount = financialData.successFeeValue;
          } else if (financialData.successFeeType === 'fixed_monthly') {
            successFeeAmount = financialData.successFeeValue * financialData.undefinedBenefitFirstInstallmentsCount;
          }

          const successFeeEntry: FinancialEntry = {
            description: `Honorários de Êxito - ${financialData.processTitle} (Benefício Vitalício)`,
            amount: successFeeAmount,
            entry_type: 'receita',
            category: 'Honorários',
            status: 'Pendente',
            client_id: financialData.clientId,
            administrative_process_id: financialData.processId,
            payment_confirmed: false,
            is_conditional_payment: true,
            conditional_type: financialData.successFeeTrigger,
            payment_notes: `Honorários sobre ${financialData.undefinedBenefitFirstInstallmentsCount} parcelas iniciais de ${financialData.undefinedBenefitFirstInstallmentValue} cada`,
            user_id: '00000000-0000-0000-0000-000000000000' // ID de usuário padrão temporário
          };
          entries.push(successFeeEntry);
          } else {
          // Para benefício normal
          const baseValue = financialData.benefitPaymentType === 'installments'
            ? financialData.benefitInstallmentValue * financialData.benefitInstallmentCount
            : financialData.benefitValue;

          let successFeeAmount = 0;
          if (financialData.successFeeType === 'percentage_global' || financialData.successFeeType === 'percentage_installment') {
            successFeeAmount = (baseValue * financialData.successFeeValue) / 100;
          } else if (financialData.successFeeType === 'fixed_global') {
            successFeeAmount = financialData.successFeeValue;
          } else if (financialData.successFeeType === 'fixed_monthly') {
            successFeeAmount = financialData.successFeeValue * financialData.benefitInstallmentCount;
          }

          const successFeeEntry: FinancialEntry = {
            description: `Honorários de Êxito - ${financialData.processTitle}`,
            amount: successFeeAmount,
            entry_type: 'receita',
            category: 'Honorários',
            status: 'Pendente',
            client_id: financialData.clientId,
            administrative_process_id: financialData.processId,
            payment_confirmed: false,
            is_conditional_payment: true,
            conditional_type: financialData.successFeeTrigger,
            payment_notes: `Honorários de êxito - ${financialData.successFeeType}`,
            user_id: '00000000-0000-0000-0000-000000000000' // ID de usuário padrão temporário
          };
          entries.push(successFeeEntry);
        }
      }

      // Criar entrada para pagamento retroativo
      if (financialData.hasRetroactivePayment && financialData.retroactivePaymentValue > 0) {
        let retroactiveFeeAmount = 0;
        if (financialData.retroactivePaymentType === 'percentage') {
          retroactiveFeeAmount = (financialData.retroactivePaymentValue * financialData.successFeeValue) / 100;
        } else {
          retroactiveFeeAmount = financialData.retroactivePaymentValue;
        }

        const retroactiveEntry: FinancialEntry = {
          description: `Honorários sobre Retroativo - ${financialData.processTitle}`,
          amount: retroactiveFeeAmount,
          entry_type: 'receita',
          category: 'Honorários',
          status: 'Pendente',
          client_id: financialData.clientId,
          administrative_process_id: financialData.processId,
          payment_confirmed: false,
          is_conditional_payment: true,
          conditional_type: 'recebimento',
          payment_notes: `Honorários sobre valor retroativo de ${financialData.retroactivePaymentValue}`,
          user_id: '00000000-0000-0000-0000-000000000000' // ID de usuário padrão temporário
        };
        entries.push(retroactiveEntry);
      }

      return entries;
    } catch (error) {
      console.error('Erro ao criar entradas financeiras do processo administrativo:', error);
      throw error;
    }
  }

  /**
   * Salva as entradas financeiras no banco de dados
   */
  static async saveFinancialEntries(entries: FinancialEntry[]): Promise<void> {
    try {
      for (const entry of entries) {
        const { error } = await supabase
          .from('financial_entries')
          .insert(entry);

        if (error) {
          throw new Error(`Erro ao salvar entrada financeira: ${error.message}`);
        }
      }
    } catch (error) {
      console.error('Erro ao salvar entradas financeiras:', error);
      throw error;
    }
  }
}